import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import communityApi from '../../api/communityApi';
import communityUrls from "../../configs/community/communityUrls";
import { PostInterface } from "../../store/community/postsSlice";
import { userEntity, usersEntity } from "./usersActions";
import { UserInterface } from "../../store/community/usersSlice";
import { addDays, addYears } from "../../helpers/DateHelpers";

const PREFIX = 'community';

const processStrategy = (value: any) => {
  return {
    ...value,
    url: communityUrls.postPath(value.id),
    file_url: communityUrls.remoteUrl(value.file_url),
    thumbnail_url: communityUrls.remoteUrl(value.thumbnail_url),
    published: new Date(value.published_at) < new Date()
  };
}

export const postsEntity = new schema.Entity(
  'posts',
  {
    user: usersEntity
  },
  {
    processStrategy
  }
);

export const postEntity = new schema.Entity(
  'post',
  {
    user: userEntity
  },
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      user: { [key: string]: UserInterface };
      post: PostInterface
    }
    >(response.data.post, postEntity);
  return normalized.entities;
}

const normalizeCollection = (response: AxiosResponse, silentId?: number) => {
  const normalized = normalize<
    any,
    {
      users: { [key: string]: UserInterface };
      posts: { [key: string]: PostInterface };
    }
    >(response.data.posts, [postsEntity]);

  return {
    users: normalized.entities.users || {},
    posts: normalized.entities?.posts || {},
    ids: normalized.result,
    count: response.data.meta?.total_count,
    silentId: silentId
  };
}

export const fetchPosts = createAsyncThunk(
  `${PREFIX}_posts/fetchPosts`,
  async ({ params, target, silentId }: { params: object; target: string; silentId?: number }) => {
    const url = `/onesky/${target}`;

    const response = await communityApi.get(url, { params });
    return normalizeCollection(response, silentId);
  }
);

export const fetchUserPosts = createAsyncThunk(
  `${PREFIX}_posts/fetchPosts`,
  async ({ userId, params = {} }: { userId: number | string; params?: object; }) => {
    const response = await communityApi.get(`onesky/users/${userId}/posts`, { params });
    return normalizeCollection(response);
  }
);

export const fetchPost = createAsyncThunk(
  `${PREFIX}_posts/fetchPost`,
  async (id: number | string) => {
    const response = await communityApi.get(`/onesky/posts/${id}`);
    return normalizeInstance(response);
  }
);

export const createPost = createAsyncThunk(
  `${PREFIX}_posts/createPost`,
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await communityApi.post(`/onesky/posts`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updatePost = createAsyncThunk(
  `${PREFIX}_posts/updatePost`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await communityApi.patch(`/onesky/posts/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const toggleShow = createAsyncThunk(
  `${PREFIX}_posts/updatePost`,
  async ({ id, published }: { id: number, published: boolean }, { rejectWithValue }) => {
    try {
      const published_at = published ? addYears(10) : addDays(0);

      const response = await communityApi.patch(`/onesky/posts/${id}`, { published_at: published_at });
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyPost = createAsyncThunk(
  `${PREFIX}_posts/destroyPost`,
  async (id: number) => {
    const response = await communityApi.delete(`/onesky/posts/${id}`);
    return response.data;
  }
);

export const resetRand = createAsyncThunk(
  `${PREFIX}_posts/resetRand`,
  async ({ params }: { params: object; }) => {
    const url = `/onesky/posts/reset_rand`;
    await communityApi.post(url, params);
  }
);
